
import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { MapPin, ChevronLeft, CreditCard } from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Card, CardContent } from '../ui/card';

// Use a valid Mapbox token (this is a temporary solution - in production, use environment variables)
const MAPBOX_TOKEN = 'pk.eyJ1IjoieWdvbG92YWNoIiwiYSI6ImNtOWE4aW44YjAyZHkyanNndGh5cHBrcGEifQ.-3qUiws_rMxwTHLJCQzI0w';

// ATM locations in Skopje, Macedonia
const ATM_LOCATIONS = [
  { name: 'Central Skopje ATM', lat: 41.9973, lng: 21.4280, address: 'Macedonia Square, Skopje' },
  { name: 'East Skopje ATM', lat: 42.0041, lng: 21.4493, address: 'Boulevard Partizanski Odredi, Skopje' },
  { name: 'West Skopje ATM', lat: 41.9937, lng: 21.4023, address: 'City Park, Skopje' }
];

const AtmMap: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [selectedAtm, setSelectedAtm] = useState<typeof ATM_LOCATIONS[0] | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  useEffect(() => {
    if (!mapContainer.current) return;

    // Initialize map
    mapboxgl.accessToken = MAPBOX_TOKEN;
    
    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [21.4280, 41.9973], // Center on Skopje
        zoom: 12,
        failIfMajorPerformanceCaveat: true,
        preserveDrawingBuffer: true
      });

      // Add navigation controls
      map.current.addControl(
        new mapboxgl.NavigationControl(),
        'top-right'
      );

      // Add ATM markers
      map.current.on('load', () => {
        setMapLoaded(true);
        console.log("Map loaded successfully");
        
        if (!map.current) return;
        
        ATM_LOCATIONS.forEach(location => {
          // Create a marker
          const markerEl = document.createElement('div');
          markerEl.className = 'atm-marker';
          markerEl.innerHTML = `<div class="bg-neobank-primary p-2 rounded-full shadow-lg cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4"><line x1="4" y1="9" x2="20" y2="9"></line><line x1="4" y1="15" x2="20" y2="15"></line><line x1="10" y1="3" x2="8" y2="21"></line><line x1="16" y1="3" x2="14" y2="21"></line></svg>
          </div>`;

          const marker = new mapboxgl.Marker(markerEl)
            .setLngLat([location.lng, location.lat])
            .setPopup(
              new mapboxgl.Popup({ offset: 25 })
                .setHTML(`
                  <h3 class="font-bold">${location.name}</h3>
                  <p>${location.address}</p>
                `)
            )
            .addTo(map.current!);

          // Handle marker click
          markerEl.addEventListener('click', () => {
            setSelectedAtm(location);
          });
        });
      });

      // Log errors
      map.current.on('error', (e) => {
        console.error('Mapbox error:', e);
        setMapError('Error loading map. Please try again later.');
      });
    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError('Could not initialize map. Please check your connection.');
    }

    // Clean up on unmount
    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="bg-neobank-primary p-4 text-white flex items-center rounded-xl mb-2">
        <button 
          onClick={() => setCurrentScreen('dashboard')}
          className="mr-2"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-bold">CoinPrime ATM Locations</h1>
      </div>

      {/* Map Container */}
      <Card className="flex-1 relative overflow-hidden mx-2 mb-2 rounded-xl">
        <div 
          ref={mapContainer} 
          className="absolute inset-0 w-full h-full"
          style={{ minHeight: '70vh' }} 
        />
        
        {!mapLoaded && !mapError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-80 z-10">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-neobank-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-gray-600">Loading map...</p>
            </div>
          </div>
        )}

        {mapError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-80 z-10">
            <div className="text-center p-4">
              <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-2">
                <p>{mapError}</p>
              </div>
              <button 
                onClick={() => window.location.reload()}
                className="bg-neobank-primary text-white py-2 px-4 rounded-lg"
              >
                Retry
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* ATM Info Panel */}
      {selectedAtm && (
        <Card className="mx-2 mb-2 rounded-xl">
          <CardContent className="p-4">
            <div className="flex items-start">
              <div className="bg-neobank-lightAccent p-2 rounded-full mr-3">
                <CreditCard className="w-5 h-5 text-neobank-primary" />
              </div>
              <div>
                <h3 className="font-bold">{selectedAtm.name}</h3>
                <p className="text-gray-600">{selectedAtm.address}</p>
                <div className="mt-2">
                  <a 
                    href={`https://www.google.com/maps/dir/?api=1&destination=${selectedAtm.lat},${selectedAtm.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-neobank-primary font-medium inline-flex items-center"
                  >
                    Get Directions
                    <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AtmMap;
